import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import { HappeningsRedirect, HappeningsCatchAll, SimpleRedirect } from "./components/LegacyRedirect";
import {
  LocalBookingRedirect,
  ExternalBookingRedirect,
  EXTERNAL_BOOKING_TARGETS,
} from "./components/booking/BookingAliasRedirect";
import { AuthProvider } from "@/hooks/useAuth";
import OvernightPopupModal from "@/components/marketing/OvernightPopupModal";
import { usePageTracking } from "@/hooks/usePageTracking";

// Lazy load non-critical pages for better initial load performance
const Cabanas = lazy(() => import("./pages/Cabanas"));
const Activities = lazy(() => import("./pages/Activities"));
const IslandMap = lazy(() => import("./pages/IslandMap"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const HoursDirections = lazy(() => import("./pages/HoursDirections"));
const BookNow = lazy(() => import("./pages/BookNow"));
const Book = lazy(() => import("./pages/Book"));
// Legacy raw-Checkfront widget pages for the five modern/protected properties
// (Geronimo, River Ranch, Blue River Camp, Rio Cibolo, Sons Island) are
// intentionally NOT imported here — their routes now redirect to guarded
// booking flows. Source files are kept in the repo to avoid breaking
// unrelated imports, but are unreachable from customer routes.
const BookSonsGuadalupe = lazy(() => import("./pages/BookSonsGuadalupe"));
const BookSonsGuadalupeOvernight = lazy(() => import("./pages/BookSonsGuadalupeOvernight"));
const About = lazy(() => import("./pages/About"));
const GiftCards = lazy(() => import("./pages/GiftCards"));
const Specials = lazy(() => import("./pages/Specials"));
const OtherProperties = lazy(() => import("./pages/OtherProperties"));
const LeadForm = lazy(() => import("./pages/LeadForm"));
const SharedWaivers = lazy(() => import("./pages/SharedWaivers"));
const Waivers = lazy(() => import("./pages/Waivers"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Pay = lazy(() => import("./pages/Pay"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostSpringBreakSonsRiverRanch = lazy(() => import("./pages/BlogPost-SpringBreakSonsRiverRanch"));
const BlogPostFunThingsToDoNearSanAntonio = lazy(() => import("./pages/BlogPost-FunThingsToDoNearSanAntonio"));
const BlogPostFunThingsToDoNearSanAntonio2026Guide = lazy(() => import("./pages/BlogPost-FunThingsToDoNearSanAntonio2026Guide"));
const BlogPostFunThingsToDoSanAntonioWeekendItinerary = lazy(() => import("./pages/BlogPost-FunThingsToDoSanAntonioWeekendItinerary"));
const BlogPostFamilyFriendlyThingsToDoSanAntonio = lazy(() => import("./pages/BlogPost-FamilyFriendlyThingsToDoSanAntonio"));
const BlogPostHoustonTxGuide2026 = lazy(() => import("./pages/BlogPost-HoustonTxGuide2026"));
const BlogPostGlampingNearSanAntonio = lazy(() => import("./pages/BlogPost-GlampingNearSanAntonio"));
const BlogPostFamilyThingsToDoNewBraunfels = lazy(() => import("./pages/BlogPost-FamilyThingsToDoNewBraunfels"));
const BlogPostFunThingsToDoNearNewBraunfels = lazy(() => import("./pages/BlogPost-FunThingsToDoNearNewBraunfels"));
const BlogPostFamilyThingsToDoNearSanAntonio = lazy(() => import("./pages/BlogPost-FamilyThingsToDoNearSanAntonio"));
const BlogPostKayakingNearNewBraunfels = lazy(() => import("./pages/BlogPost-KayakingNearNewBraunfels"));
const BlogPostPaddleBoardingNearNewBraunfels = lazy(() => import("./pages/BlogPost-PaddleBoardingNearNewBraunfels"));
const BlogPostKayakingNearSanAntonioGuide = lazy(() => import("./pages/BlogPost-KayakingNearSanAntonioGuide"));
const BlogPostPaddleBoardingNearSanAntonioSpots = lazy(() => import("./pages/BlogPost-PaddleBoardingNearSanAntonioSpots"));
const BlogPostCabanaRentalsNearSanAntonio = lazy(() => import("./pages/BlogPost-CabanaRentalsNearSanAntonio"));
const BlogPostLakeDayFunNearSanAntonio = lazy(() => import("./pages/BlogPost-LakeDayFunNearSanAntonio"));
const BlogPostFamilyGetawaysWeekend = lazy(() => import("./pages/BlogPost-FamilyGetawaysWeekend"));
const BlogPostLakePlacidTexasGuide = lazy(() => import("./pages/BlogPost-LakePlacidTexasGuide"));
const BlogPostWeekendGetawaysFromSanAntonio = lazy(() => import("./pages/BlogPost-WeekendGetawaysFromSanAntonio"));
const BlogPostRomanticGetawaysNearSanAntonio = lazy(() => import("./pages/BlogPost-RomanticGetawaysNearSanAntonio"));
const BlogPostPrivateIslandRentalNearSanAntonio = lazy(() => import("./pages/BlogPost-PrivateIslandRentalNearSanAntonio"));
const BlogPostGuadalupeRiverWeekendItinerary = lazy(() => import("./pages/BlogPost-GuadalupeRiverWeekendItinerary"));
const BlogPostCrowdFreeSwimmingCentralTexas = lazy(() => import("./pages/BlogPost-CrowdFreeSwimmingCentralTexas"));
const BlogPostCorporateLargeGroupWaterfrontVenues = lazy(() => import("./pages/BlogPost-CorporateLargeGroupWaterfrontVenues"));
const AvoidTheCrowdsPrivateIslandAccess = lazy(() => import("./pages/AvoidTheCrowdsPrivateIslandAccess"));
const GroupsAndEvents = lazy(() => import("./pages/GroupsAndEvents"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ThingsToDoNearSanAntonio = lazy(() => import("./pages/ThingsToDoNearSanAntonio"));
const FamilyGetawaysNearSanAntonio = lazy(() => import("./pages/FamilyGetawaysNearSanAntonio"));
const KayakingNearSanAntonio = lazy(() => import("./pages/KayakingNearSanAntonio"));
const PaddleBoardingNearSanAntonio = lazy(() => import("./pages/PaddleBoardingNearSanAntonio"));
const PaddleBoardingNearNewBraunfels = lazy(() => import("./pages/PaddleBoardingNearNewBraunfels"));
const PaddleBoardingSeguin = lazy(() => import("./pages/PaddleBoardingSeguin"));
const GlampingNearSanAntonio = lazy(() => import("./pages/GlampingNearSanAntonio"));
const GlampingNearNewBraunfels = lazy(() => import("./pages/GlampingNearNewBraunfels"));
const ThingsToDoNewBraunfels = lazy(() => import("./pages/ThingsToDoNewBraunfels"));
const ThingsToDoSeguin = lazy(() => import("./pages/ThingsToDoSeguin"));
const DayTripsFromAustin = lazy(() => import("./pages/DayTripsFromAustin"));
const ThingsToDoSanMarcos = lazy(() => import("./pages/ThingsToDoSanMarcos"));
const CabanaRentalsNewBraunfels = lazy(() => import("./pages/CabanaRentalsNewBraunfels"));
const CabanaRentalsSanAntonio = lazy(() => import("./pages/CabanaRentalsSanAntonio"));
const CabanaRentalsAustin = lazy(() => import("./pages/CabanaRentalsAustin"));
const ThingsToDoNearHouston = lazy(() => import("./pages/ThingsToDoNearHouston"));
const GlampingAustin = lazy(() => import("./pages/GlampingAustin"));
const KayakingSeguin = lazy(() => import("./pages/KayakingSeguin"));
const KayakingNearNewBraunfels = lazy(() => import("./pages/KayakingNearNewBraunfels"));
const FamilyGetawaysNearNewBraunfels = lazy(() => import("./pages/FamilyGetawaysNearNewBraunfels"));
const FamilyGetawaysNearHouston = lazy(() => import("./pages/FamilyGetawaysNearHouston"));
const FamilyGetawaysSeguin = lazy(() => import("./pages/FamilyGetawaysSeguin"));
const FamilyGetawaysNearSanMarcos = lazy(() => import("./pages/FamilyGetawaysNearSanMarcos"));
const ThingsToDoNearAustin = lazy(() => import("./pages/ThingsToDoNearAustin"));
const DayTripsFromSanAntonio = lazy(() => import("./pages/DayTripsFromSanAntonio"));
const DayTripsFromNewBraunfels = lazy(() => import("./pages/DayTripsFromNewBraunfels"));
const DayTripsNearHouston = lazy(() => import("./pages/DayTripsNearHouston"));
const DayTripsNearSanMarcos = lazy(() => import("./pages/DayTripsNearSanMarcos"));
const TubingNearSanAntonio = lazy(() => import("./pages/TubingNearSanAntonio"));
const DayTripPlanningGuide = lazy(() => import("./pages/DayTripPlanningGuide"));
const PrivateIslandDayTripSanAntonio = lazy(() => import("./pages/PrivateIslandDayTripSanAntonio"));
const GlampingVsSanAntonioResorts = lazy(() => import("./pages/GlampingVsSanAntonioResorts"));
const NearbyGlamping = lazy(() => import("./pages/NearbyGlamping"));
const Glamping = lazy(() => import("./pages/Glamping"));
const PaddlingAtSonsIsland = lazy(() => import("./pages/PaddlingAtSonsIsland"));
const SiteMap = lazy(() => import("./pages/SiteMap"));
const SitemapSitelinks = lazy(() => import("./pages/SitemapSitelinks"));
const Photos = lazy(() => import("./pages/Photos"));
const Images5 = lazy(() => import("./pages/Images5"));
const Map5 = lazy(() => import("./pages/Map5"));
const Map5Editor = lazy(() => import("./pages/Map5Editor"));
const Map5EditorNumbered = lazy(() => import("./pages/Map5EditorNumbered"));
const CarouselOptions = lazy(() => import("./pages/CarouselOptions"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const RouteTracker = () => {
  usePageTracking();
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
          <RouteTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/cabanas" element={<Cabanas />} />
              <Route path="/activities" element={<Activities />} />
              <Route path="/island-map" element={<IslandMap />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/book-now" element={<Book />} />
              <Route path="/book" element={<Book />} />
              {/* Legacy raw-widget aliases now redirect. Guadalupe untouched. */}
              <Route path="/book-now/geronimo" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.geronimo} />} />
              <Route path="/book-now/river-ranch" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.riverRanch} />} />
              <Route path="/book-now/river-ranch-overnight" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.riverRanch} />} />
              <Route path="/book-now/blue-river-camp" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.blueRiver} />} />
              <Route path="/book-now/blue-river-camp-overnight" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.blueRiver} />} />
              <Route path="/book-now/rio-cibolo" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.rioCibolo} />} />
              <Route path="/book-now/rio-cibolo-overnight" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.rioCibolo} />} />
              <Route path="/book-now/sons-island" element={<LocalBookingRedirect />} />
              <Route path="/book-now/sons-island-overnight" element={<LocalBookingRedirect intent="overnight" />} />
              <Route path="/book-now/sons-guadalupe" element={<BookSonsGuadalupe />} />
              <Route path="/book-now/sons-guadalupe-overnight" element={<BookSonsGuadalupeOvernight />} />
              {/* /bookfallback/* mirrors /book-now/*. Protected properties redirect; Guadalupe unchanged. */}
              <Route path="/bookfallback" element={<BookNow />} />
              <Route path="/bookfallback/geronimo" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.geronimo} />} />
              <Route path="/bookfallback/river-ranch" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.riverRanch} />} />
              <Route path="/bookfallback/river-ranch-overnight" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.riverRanch} />} />
              <Route path="/bookfallback/blue-river-camp" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.blueRiver} />} />
              <Route path="/bookfallback/blue-river-camp-overnight" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.blueRiver} />} />
              <Route path="/bookfallback/rio-cibolo" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.rioCibolo} />} />
              <Route path="/bookfallback/rio-cibolo-overnight" element={<ExternalBookingRedirect url={EXTERNAL_BOOKING_TARGETS.rioCibolo} />} />
              <Route path="/bookfallback/sons-island" element={<LocalBookingRedirect />} />
              <Route path="/bookfallback/sons-island-overnight" element={<LocalBookingRedirect intent="overnight" />} />
              <Route path="/bookfallback/sons-guadalupe" element={<BookSonsGuadalupe />} />
              <Route path="/bookfallback/sons-guadalupe-overnight" element={<BookSonsGuadalupeOvernight />} />
              <Route path="/about" element={<About />} />
              <Route path="/specials" element={<Specials />} />
              <Route path="/gift-cards" element={<GiftCards />} />
              <Route path="/waivers" element={<Waivers />} />
              <Route path="/hours-directions" element={<HoursDirections />} />
              <Route path="/other-properties" element={<OtherProperties />} />
              <Route path="/inquire" element={<LeadForm />} />
              <Route path="/sharedwaivers" element={<SharedWaivers />} />
              <Route path="/thankyou" element={<ThankYou />} />
              <Route path="/pay/:bookingCode" element={<Pay />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/spring-break-sons-river-ranch" element={<BlogPostSpringBreakSonsRiverRanch />} />
              <Route path="/blog/fun-things-to-do-near-san-antonio" element={<BlogPostFunThingsToDoNearSanAntonio />} />
              <Route path="/blog/fun-things-to-do-near-san-antonio-2026-guide" element={<BlogPostFunThingsToDoNearSanAntonio2026Guide />} />
              <Route path="/blog/fun-things-to-do-san-antonio-weekend-itinerary" element={<BlogPostFunThingsToDoSanAntonioWeekendItinerary />} />
              <Route path="/blog/family-friendly-things-to-do-san-antonio" element={<BlogPostFamilyFriendlyThingsToDoSanAntonio />} />
              <Route path="/blog/houston-tx-guide-2026" element={<BlogPostHoustonTxGuide2026 />} />
              <Route path="/blog/glamping-near-san-antonio" element={<BlogPostGlampingNearSanAntonio />} />
              <Route path="/blog/family-things-to-do-new-braunfels" element={<BlogPostFamilyThingsToDoNewBraunfels />} />
              <Route path="/blog/fun-things-to-do-near-new-braunfels" element={<BlogPostFunThingsToDoNearNewBraunfels />} />
              <Route path="/blog/family-things-to-do-near-san-antonio" element={<BlogPostFamilyThingsToDoNearSanAntonio />} />
              <Route path="/blog/kayaking-near-new-braunfels" element={<BlogPostKayakingNearNewBraunfels />} />
              <Route path="/blog/paddle-boarding-near-new-braunfels" element={<BlogPostPaddleBoardingNearNewBraunfels />} />
              <Route path="/blog/kayaking-near-san-antonio-guide" element={<BlogPostKayakingNearSanAntonioGuide />} />
              <Route path="/blog/paddle-boarding-near-san-antonio-spots" element={<BlogPostPaddleBoardingNearSanAntonioSpots />} />
              <Route path="/blog/cabana-rentals-near-san-antonio" element={<BlogPostCabanaRentalsNearSanAntonio />} />
              <Route path="/blog/lake-day-fun-near-san-antonio" element={<BlogPostLakeDayFunNearSanAntonio />} />
              <Route path="/blog/family-getaways-near-san-antonio-weekend" element={<BlogPostFamilyGetawaysWeekend />} />
              <Route path="/blog/lake-placid-texas-guide" element={<BlogPostLakePlacidTexasGuide />} />
              <Route path="/blog/weekend-getaways-from-san-antonio" element={<BlogPostWeekendGetawaysFromSanAntonio />} />
              <Route path="/blog/romantic-getaways-near-san-antonio" element={<BlogPostRomanticGetawaysNearSanAntonio />} />
              <Route path="/blog/private-island-rental-near-san-antonio" element={<BlogPostPrivateIslandRentalNearSanAntonio />} />
              <Route path="/blog/guadalupe-river-weekend-itinerary" element={<BlogPostGuadalupeRiverWeekendItinerary />} />
              <Route path="/blog/crowd-free-swimming-central-texas" element={<BlogPostCrowdFreeSwimmingCentralTexas />} />
              <Route path="/blog/corporate-large-group-waterfront-venues-san-antonio" element={<BlogPostCorporateLargeGroupWaterfrontVenues />} />
              <Route path="/avoid-the-crowds-private-island-access" element={<AvoidTheCrowdsPrivateIslandAccess />} />
              <Route path="/groups-and-events" element={<GroupsAndEvents />} />
              <Route path="/things-to-do-near-san-antonio" element={<ThingsToDoNearSanAntonio />} />
              <Route path="/family-getaways-near-san-antonio" element={<FamilyGetawaysNearSanAntonio />} />
              <Route path="/kayaking-near-san-antonio" element={<KayakingNearSanAntonio />} />
              <Route path="/paddle-boarding-near-san-antonio" element={<PaddleBoardingNearSanAntonio />} />
              <Route path="/paddle-boarding-near-new-braunfels" element={<PaddleBoardingNearNewBraunfels />} />
              <Route path="/paddle-boarding-seguin" element={<PaddleBoardingSeguin />} />
              <Route path="/glamping-near-san-antonio" element={<GlampingNearSanAntonio />} />
              <Route path="/glamping-near-new-braunfels" element={<GlampingNearNewBraunfels />} />
              <Route path="/things-to-do-new-braunfels" element={<ThingsToDoNewBraunfels />} />
              <Route path="/things-to-do-seguin" element={<ThingsToDoSeguin />} />
              <Route path="/day-trips-from-austin" element={<DayTripsFromAustin />} />
              <Route path="/things-to-do-san-marcos" element={<ThingsToDoSanMarcos />} />
              <Route path="/cabana-rentals-new-braunfels" element={<CabanaRentalsNewBraunfels />} />
              <Route path="/cabana-rentals-san-antonio" element={<CabanaRentalsSanAntonio />} />
              <Route path="/cabana-rentals-austin" element={<CabanaRentalsAustin />} />
              <Route path="/things-to-do-near-houston" element={<ThingsToDoNearHouston />} />
              <Route path="/glamping-austin" element={<GlampingAustin />} />
              <Route path="/kayaking-seguin" element={<KayakingSeguin />} />
              <Route path="/kayaking-near-new-braunfels" element={<KayakingNearNewBraunfels />} />
              <Route path="/family-getaways-near-new-braunfels" element={<FamilyGetawaysNearNewBraunfels />} />
              <Route path="/family-getaways-near-houston" element={<FamilyGetawaysNearHouston />} />
              <Route path="/family-getaways-seguin" element={<FamilyGetawaysSeguin />} />
              <Route path="/family-getaways-near-san-marcos" element={<FamilyGetawaysNearSanMarcos />} />
              <Route path="/things-to-do-near-austin" element={<ThingsToDoNearAustin />} />
              <Route path="/day-trips-from-san-antonio" element={<DayTripsFromSanAntonio />} />
              <Route path="/day-trips-from-new-braunfels" element={<DayTripsFromNewBraunfels />} />
              <Route path="/day-trips-near-houston" element={<DayTripsNearHouston />} />
              <Route path="/day-trips-near-san-marcos" element={<DayTripsNearSanMarcos />} />
              <Route path="/tubing-near-san-antonio" element={<TubingNearSanAntonio />} />
              <Route path="/day-trip-planning-guide" element={<DayTripPlanningGuide />} />
              <Route path="/private-island-day-trip-san-antonio" element={<PrivateIslandDayTripSanAntonio />} />
              <Route path="/glamping-vs-san-antonio-resorts" element={<GlampingVsSanAntonioResorts />} />
              <Route path="/nearby-glamping" element={<NearbyGlamping />} />
              <Route path="/glamping" element={<Glamping />} />
              <Route path="/paddling-at-sons-island" element={<PaddlingAtSonsIsland />} />
              <Route path="/sitemap" element={<SiteMap />} />
              <Route path="/sitemapsitelinks" element={<SitemapSitelinks />} />
              <Route path="/photos" element={<Photos />} />
              <Route path="/images5" element={<Images5 />} />
              <Route path="/control" element={<Navigate to="/" replace />} />
              <Route path="/map5" element={<Map5 />} />
              <Route path="/map5/editor" element={<Map5Editor />} />
              <Route path="/map5/editor/numbered" element={<Map5EditorNumbered />} />
              <Route path="/carousel" element={<CarouselOptions />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              {/* Legacy Squarespace URL redirects (still indexed in GSC) */}
              <Route path="/happenings/f/:slug" element={<HappeningsRedirect />} />
              <Route path="/happenings/*" element={<HappeningsCatchAll />} />
              <Route path="/faqs" element={<SimpleRedirect to="/faq" />} />
              <Route path="/kayak-&-board-rentals" element={<SimpleRedirect to="/activities" />} />
              <Route path="/kayak-board-rentals" element={<SimpleRedirect to="/activities" />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <OvernightPopupModal />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
